import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import LockIcon from '@mui/icons-material/Lock';
import {
  getTestStatus,
  startAssessmentTest,
  submitAssessmentTest,
} from '../services/internApi';

const PASSED_GRADIENT = 'linear-gradient(135deg, #16a34a, #22c55e)';
const FAILED_GRADIENT = 'linear-gradient(135deg, #dc2626, #ef4444)';
const NEUTRAL_GRADIENT = 'linear-gradient(135deg, #1e3a8a, #7c3aed)';

function formatRemaining(seconds) {
  if (seconds == null || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatRelativeDate(isoStr) {
  if (!isoStr) return null;
  try {
    const d = new Date(isoStr.replace('Z', ''));
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

// Lightweight CSS-only confetti. Renders a fixed-position layer with 80
// absolutely positioned pieces that fall via keyframes (declared inline).
function Confetti() {
  const pieces = useMemo(() => {
    const colors = ['#fbbf24', '#f87171', '#34d399', '#60a5fa', '#a78bfa', '#f472b6'];
    return Array.from({ length: 90 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2.5 + Math.random() * 2,
      color: colors[i % colors.length],
      rotateStart: Math.random() * 360,
      size: 6 + Math.random() * 8,
    }));
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      <style>{`
        @keyframes cwc-fall {
          0% { transform: translate3d(0, -10vh, 0) rotate(var(--rs, 0deg)); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(var(--drift, 30px), 110vh, 0) rotate(720deg); opacity: 0.8; }
        }
      `}</style>
      {pieces.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            backgroundColor: p.color,
            borderRadius: 1,
            animation: `cwc-fall ${p.duration}s linear ${p.delay}s forwards`,
            '--rs': `${p.rotateStart}deg`,
            '--drift': `${(Math.random() - 0.5) * 200}px`,
          }}
        />
      ))}
    </Box>
  );
}

export default function AssessmentTestPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [phase, setPhase] = useState('loading'); // loading | intro | in_progress | review | results | locked
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);

  // In-progress test state
  const [testId, setTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});       // {qid: 'a'|'b'|'c'|'d'}
  const [flags, setFlags] = useState({});           // {qid: true}
  const [currentIdx, setCurrentIdx] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // Results
  const [result, setResult] = useState(null);

  const timerRef = useRef(null);
  const autoSubmittedRef = useRef(false);

  // ---- Initial load ----
  const loadStatus = useCallback(async () => {
    setPhase('loading');
    setError('');
    try {
      const s = await getTestStatus();
      setStatus(s);
      if (s.in_progress) {
        // Resume — fetch the questions
        await beginTest(true);
      } else if (s.status === 'passed') {
        setPhase('results');
        setResult({
          passed: true,
          score: s.last_score,
          total: s.questions_per_test,
          percentage: s.last_percentage,
          per_domain: s.last_domain_scores,
          attempt_number: s.attempt_count,
          review: [],
        });
      } else if (s.permanently_ineligible) {
        setPhase('locked');
      } else {
        setPhase('intro');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load test status');
      setPhase('intro');
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // ---- Start / resume ----
  async function beginTest(resume) {
    setError('');
    setPhase('loading');
    try {
      const data = await startAssessmentTest();
      setTestId(data.test_id);
      setQuestions(data.questions || []);
      setExpiresAt(data.expires_at);
      setAnswers({});
      setFlags({});
      setCurrentIdx(0);
      autoSubmittedRef.current = false;
      // Compute remaining
      const ms = new Date(data.expires_at.replace('Z', '')).getTime() - Date.now();
      setRemainingSeconds(Math.max(0, Math.floor(ms / 1000)));
      setPhase('in_progress');
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to start test';
      setError(msg);
      setPhase('intro');
    }
  }

  // ---- Timer ----
  useEffect(() => {
    if (phase !== 'in_progress' && phase !== 'review') return undefined;
    if (!expiresAt) return undefined;

    const tick = () => {
      const ms = new Date(expiresAt.replace('Z', '')).getTime() - Date.now();
      const secs = Math.max(0, Math.floor(ms / 1000));
      setRemainingSeconds(secs);
      if (secs <= 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        handleSubmit({ auto: true });
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, expiresAt]);

  // ---- Submit ----
  async function handleSubmit({ auto = false } = {}) {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const data = await submitAssessmentTest(testId, answers);
      setResult({ ...data, auto });
      setPhase('results');
      // Refresh status data so retake banner reflects new state
      try {
        const s = await getTestStatus();
        setStatus(s);
      } catch (_) {}
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to submit test');
    } finally {
      setSubmitting(false);
      setConfirmSubmit(false);
    }
  }

  // ---- Render branches ----
  if (phase === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (phase === 'intro') {
    return <IntroView status={status} error={error} onStart={() => beginTest(false)} />;
  }

  if (phase === 'locked') {
    return <LockedView />;
  }

  if (phase === 'review') {
    return (
      <ReviewView
        questions={questions}
        answers={answers}
        flags={flags}
        remainingSeconds={remainingSeconds}
        onBack={() => setPhase('in_progress')}
        onJumpTo={(idx) => { setCurrentIdx(idx); setPhase('in_progress'); }}
        onSubmit={() => setConfirmSubmit(true)}
        confirmSubmit={confirmSubmit}
        onConfirm={() => handleSubmit({ auto: false })}
        onCancelConfirm={() => setConfirmSubmit(false)}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (phase === 'results') {
    return (
      <ResultsView
        result={result}
        status={status}
        onReturnToDashboard={() => navigate('/')}
        onRetry={() => beginTest(false)}
      />
    );
  }

  // phase === 'in_progress'
  return (
    <InProgressView
      isMobile={isMobile}
      questions={questions}
      currentIdx={currentIdx}
      setCurrentIdx={setCurrentIdx}
      answers={answers}
      setAnswers={setAnswers}
      flags={flags}
      setFlags={setFlags}
      remainingSeconds={remainingSeconds}
      onReview={() => setPhase('review')}
      error={error}
    />
  );
}

// ---- IntroView (Start button + retake info) ----
function IntroView({ status, error, onStart }) {
  const attemptNumber = (status?.attempt_count || 0) + 1;
  const weakDomains = status?.last_weak_domains || [];
  const showRetakeBanner = status?.attempt_count > 0 && !status?.permanently_ineligible;
  const showLockedBanner = status?.next_eligible_at && status?.attempt_count > 0;
  const nowMs = Date.now();
  const lockedUntilMs = status?.next_eligible_at
    ? new Date(String(status.next_eligible_at).replace('Z', '')).getTime()
    : null;
  const isLocked = lockedUntilMs && lockedUntilMs > nowMs;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Onboarding Test</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        A 40-question, 45-minute timed cybersecurity test. You need <strong>75%</strong> to advance.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {showRetakeBanner && (
        <Alert severity={isLocked ? 'warning' : 'info'} sx={{ mb: 3 }}>
          <AlertTitle>
            Attempt {attemptNumber} of 4
          </AlertTitle>
          {weakDomains.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <strong>Focus on:</strong> {weakDomains.join(', ')}
            </Box>
          )}
          {isLocked && (
            <Box>
              You can retake the test on <strong>{formatRelativeDate(status.next_eligible_at)}</strong>.
            </Box>
          )}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>What to expect</Typography>
        <Stack spacing={1.5}>
          <RowItem label="Questions">40 randomly selected from a bank of 400</RowItem>
          <RowItem label="Time limit">45 minutes (auto-submits when timer hits 0)</RowItem>
          <RowItem label="Passing score">30 / 40 (75%)</RowItem>
          <RowItem label="Retakes">Up to 4 attempts total. Cooldowns: 48h, then 1 week, then 1 month.</RowItem>
          <RowItem label="Domains">
            Network &amp; Router · Devices &amp; Endpoints · Accounts &amp; Auth · Data &amp; Backups · Privacy &amp; Online Safety · Physical Security · General Knowledge
          </RowItem>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          You can navigate freely between questions, flag any to revisit,
          and review all answers before submitting. Once you click <em>Start</em>, the timer begins.
        </Typography>
      </Paper>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onStart}
        disabled={isLocked || status?.permanently_ineligible}
        sx={{
          py: 1.5,
          background: NEUTRAL_GRADIENT,
          fontWeight: 600,
        }}
      >
        {status?.attempt_count > 0 ? 'Start Retake' : 'Start Test'}
      </Button>
    </Box>
  );
}

function RowItem({ label, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

// ---- LockedView (permanently_ineligible) ----
function LockedView() {
  return (
    <Box sx={{ textAlign: 'center', py: 6, maxWidth: 480, mx: 'auto' }}>
      <LockIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h4" sx={{ mb: 1 }}>You are no longer eligible</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        You have exceeded the maximum number of test attempts. Reach out to support if you'd like to discuss other opportunities.
      </Typography>
      <Button variant="outlined" href="mailto:support@cyberwisecorp.com">
        Contact Support
      </Button>
    </Box>
  );
}

// ---- InProgressView (question + nav) ----
function InProgressView({
  isMobile,
  questions, currentIdx, setCurrentIdx,
  answers, setAnswers,
  flags, setFlags,
  remainingSeconds, onReview, error,
}) {
  const q = questions[currentIdx];
  if (!q) return null;

  const total = questions.length;
  const answeredCount = Object.keys(answers).length;
  const isFlagged = !!flags[q.id];
  const isLow = remainingSeconds != null && remainingSeconds < 300; // last 5 min

  function selectAnswer(choice) {
    setAnswers((prev) => ({ ...prev, [q.id]: choice }));
  }

  function toggleFlag() {
    setFlags((prev) => {
      const next = { ...prev };
      if (next[q.id]) delete next[q.id]; else next[q.id] = true;
      return next;
    });
  }

  function gotoIdx(idx) {
    setCurrentIdx(Math.max(0, Math.min(total - 1, idx)));
  }

  return (
    <Box>
      {/* Timer + progress sticky header */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: isLow ? 'rgba(254, 226, 226, 0.95)' : 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(6px)',
          borderLeft: isLow ? '4px solid #dc2626' : '4px solid #2563eb',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimerIcon sx={{ color: isLow ? 'error.main' : 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: isLow ? 'error.main' : 'text.primary', fontFamily: 'monospace' }}>
              {formatRemaining(remainingSeconds)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {isLow ? 'Hurry — last few minutes!' : 'Time remaining'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            <strong>Question {currentIdx + 1}</strong> of {total} · {answeredCount} answered
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(currentIdx + 1) / total * 100}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Main question */}
        <Grid item xs={12} md={isMobile ? 12 : 8}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Chip label={q.domain.replace(/_/g, ' ')} size="small" sx={{ textTransform: 'capitalize' }} />
              <Button
                size="small"
                onClick={toggleFlag}
                startIcon={isFlagged ? <FlagIcon /> : <FlagOutlinedIcon />}
                color={isFlagged ? 'warning' : 'inherit'}
              >
                {isFlagged ? 'Flagged' : 'Flag for review'}
              </Button>
            </Box>
            <Typography variant="h6" sx={{ mb: 3, lineHeight: 1.4 }}>{q.question}</Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={answers[q.id] || ''}
                onChange={(e) => selectAnswer(e.target.value)}
              >
                {['a', 'b', 'c', 'd'].map((letter) => (
                  <Paper
                    key={letter}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      mb: 1,
                      cursor: 'pointer',
                      borderColor: answers[q.id] === letter ? 'primary.main' : 'divider',
                      backgroundColor: answers[q.id] === letter ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.04)' },
                    }}
                    onClick={() => selectAnswer(letter)}
                  >
                    <FormControlLabel
                      value={letter}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography component="span" sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>
                            {letter.toUpperCase()}.
                          </Typography>
                          <Typography component="span">{q.options[letter]}</Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => gotoIdx(currentIdx - 1)}
                disabled={currentIdx === 0}
              >
                Previous
              </Button>
              {currentIdx < total - 1 ? (
                <Button variant="contained" onClick={() => gotoIdx(currentIdx + 1)}>
                  Next
                </Button>
              ) : (
                <Button variant="contained" color="success" onClick={onReview}>
                  Review &amp; Submit
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Question grid sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Question Map</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Click any number to jump. Yellow = flagged. Filled = answered.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.75 }}>
              {questions.map((qq, idx) => {
                const answered = !!answers[qq.id];
                const flagged = !!flags[qq.id];
                const isCurrent = idx === currentIdx;
                let bg = 'background.paper';
                let color = 'text.primary';
                let border = '1px solid';
                let borderColor = 'divider';
                if (flagged) { bg = '#fef3c7'; borderColor = '#f59e0b'; }
                if (answered && !flagged) { bg = '#dbeafe'; borderColor = '#2563eb'; color = '#1e40af'; }
                if (answered && flagged) { bg = '#fde68a'; borderColor = '#d97706'; }
                if (isCurrent) { borderColor = '#7c3aed'; border = '2px solid'; }
                return (
                  <Box
                    key={qq.id}
                    onClick={() => gotoIdx(idx)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      border,
                      borderColor,
                      bgcolor: bg,
                      color,
                      textAlign: 'center',
                      py: 0.75,
                      fontSize: 12,
                      fontWeight: 600,
                      userSelect: 'none',
                    }}
                  >
                    {idx + 1}
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={onReview}
            >
              Review &amp; Submit
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ---- ReviewView ----
function ReviewView({
  questions, answers, flags,
  remainingSeconds, onBack, onJumpTo, onSubmit,
  confirmSubmit, onConfirm, onCancelConfirm, submitting, error,
}) {
  const total = questions.length;
  const unanswered = questions.filter((q) => !answers[q.id]).length;
  const flagged = questions.filter((q) => flags[q.id]).length;
  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Review Your Answers</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${total - unanswered}/${total} answered`} color="primary" />
          <Chip label={`${unanswered} unanswered`} color={unanswered > 0 ? 'warning' : 'default'} />
          <Chip label={`${flagged} flagged`} color="default" />
          <Chip
            icon={<TimerIcon />}
            label={formatRemaining(remainingSeconds)}
            color={remainingSeconds < 300 ? 'error' : 'default'}
            sx={{ fontFamily: 'monospace' }}
          />
        </Stack>
        {unanswered > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have <strong>{unanswered} unanswered question{unanswered === 1 ? '' : 's'}</strong>. Unanswered counts as wrong.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onBack} fullWidth>
            Back to Questions
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={onSubmit}
            fullWidth
            disabled={submitting}
          >
            Submit Test
          </Button>
        </Stack>
      </Paper>

      <Stack spacing={1.5}>
        {questions.map((q, idx) => {
          const a = answers[q.id];
          return (
            <Paper
              key={q.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderLeft: '4px solid',
                borderLeftColor: a ? 'success.main' : 'warning.main',
                cursor: 'pointer',
              }}
              onClick={() => onJumpTo(idx)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Q{idx + 1} · {q.domain.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {q.question}
                  </Typography>
                  <Typography variant="body2" color={a ? 'text.primary' : 'warning.main'}>
                    {a ? `Your answer: ${a.toUpperCase()}. ${q.options[a]}` : 'Not answered'}
                  </Typography>
                </Box>
                {flags[q.id] && <FlagIcon color="warning" />}
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Dialog open={confirmSubmit} onClose={onCancelConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Submit your test?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You answered <strong>{total - unanswered}</strong> of <strong>{total}</strong> questions.
            Unanswered questions count as wrong. You cannot change your answers after submitting.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You need 75% (30/40) to pass.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelConfirm}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Yes, Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---- ResultsView ----
function ResultsView({ result, status, onReturnToDashboard, onRetry }) {
  const navigate = useNavigate();
  const passed = result?.passed;
  const showConfetti = passed && result?.review?.length > 0; // skip confetti when we came in via already-passed status
  const total = result?.total || 40;
  const score = result?.score ?? 0;
  const pct = result?.percentage ?? 0;
  const passPct = result?.pass_percentage ?? 75;
  const perDomain = result?.per_domain || {};
  const weakDomains = result?.weak_domains || [];
  const permanentlyIneligible = result?.permanently_ineligible;
  const nextEligibleAt = result?.next_eligible_at;
  const attemptNumber = result?.attempt_number || status?.attempt_count || 0;
  const review = result?.review || [];

  return (
    <Box>
      {showConfetti && <Confetti />}
      <Paper
        sx={{
          p: { xs: 3, sm: 5 },
          background: passed ? PASSED_GRADIENT : FAILED_GRADIENT,
          color: 'white',
          textAlign: 'center',
          mb: 3,
        }}
      >
        {passed ? (
          <EmojiEventsIcon sx={{ fontSize: 64, mb: 1 }} />
        ) : (
          <SentimentDissatisfiedIcon sx={{ fontSize: 64, mb: 1 }} />
        )}
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          {passed ? 'Congratulations!' : 'Not Quite There Yet'}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.95, mb: 2 }}>
          {passed
            ? "You passed the cybersecurity onboarding test. Next step: upload your introduction video."
            : permanentlyIneligible
              ? 'You have used all 4 test attempts.'
              : 'Review your weak areas and try again after the cooldown.'}
        </Typography>
        <Box sx={{ display: 'inline-block', background: 'rgba(0,0,0,0.2)', borderRadius: 2, px: 3, py: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
            {score} / {total} ({pct}%)
          </Typography>
          <Typography variant="caption">Passing: {passPct}%</Typography>
        </Box>
      </Paper>

      {/* Per-domain breakdown */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Per-Domain Breakdown</Typography>
        <Stack spacing={1.5}>
          {Object.entries(perDomain).map(([key, d]) => {
            const dPct = d.percentage || 0;
            const weak = dPct < passPct;
            return (
              <Box key={key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.name || key}
                    {weak && <Chip label="Focus area" size="small" color="error" sx={{ ml: 1, height: 20 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {d.correct}/{d.total} ({dPct}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, dPct)}
                  color={weak ? 'error' : 'success'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {!passed && !permanentlyIneligible && nextEligibleAt && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Cooldown</AlertTitle>
          You can retake the test on <strong>{formatRelativeDate(nextEligibleAt)}</strong>.
          {weakDomains.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <strong>Focus on:</strong> {weakDomains.join(', ')}
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            Attempt {attemptNumber} of 4 used.
          </Box>
        </Alert>
      )}
      {permanentlyIneligible && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Maximum attempts reached</AlertTitle>
          We're sorry, you have exceeded the maximum number of attempts. Contact{' '}
          <a href="mailto:support@cyberwisecorp.com">support@cyberwisecorp.com</a> if you'd like to discuss other opportunities.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {passed && (
          <Button variant="contained" size="large" fullWidth onClick={() => navigate('/profile')}>
            Continue to Video Upload
          </Button>
        )}
        <Button variant="outlined" size="large" fullWidth onClick={onReturnToDashboard}>
          Return to Dashboard
        </Button>
      </Stack>

      {/* Detailed review (only on a fresh submission, not on already-passed reload) */}
      {review.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Review every question</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Below is the correct answer + an explanation for each of the 40 questions you saw.
          </Typography>
          <Stack spacing={2}>
            {review.map((r, idx) => (
              <Paper
                key={r.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderLeft: '4px solid',
                  borderLeftColor: r.is_correct ? 'success.main' : 'error.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {r.is_correct
                    ? <CheckCircleIcon color="success" fontSize="small" />
                    : <CancelIcon color="error" fontSize="small" />}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Q{idx + 1} · {(r.domain || '').replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {r.question}
                </Typography>
                <Box sx={{ mb: 1, pl: 1, borderLeft: '3px solid #e5e7eb' }}>
                  <Typography variant="caption" color="text.secondary">Your answer:</Typography>
                  <Typography variant="body2" color={r.is_correct ? 'success.main' : 'error.main'}>
                    {r.user_answer
                      ? `${r.user_answer.toUpperCase()}. ${r.options?.[r.user_answer] || ''}`
                      : 'Not answered'}
                  </Typography>
                </Box>
                {!r.is_correct && (
                  <Box sx={{ mb: 1, pl: 1, borderLeft: '3px solid #16a34a' }}>
                    <Typography variant="caption" color="text.secondary">Correct answer:</Typography>
                    <Typography variant="body2" color="success.main">
                      {r.correct_answer.toUpperCase()}. {r.options?.[r.correct_answer] || ''}
                    </Typography>
                  </Box>
                )}
                {r.explanation && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {r.explanation}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
